/** Pure derivation of a per-row "change tag" from a row's LaneRowFacts. Used by
 *  the publish ledger's Changes-only view to label each changed row and to
 *  decide which rows are unchanged (and thus hidden). No React, no IO. */
import type { LaneRowFacts } from "../ScopeLedger";

export type ChangeKind =
  | "new"
  | "deleted"
  | "rescheduled"
  | "renamed"
  | "recoded"
  | "edited";

export interface ChangeTag {
  kind: ChangeKind;
  label: string;
}

/** Does this row carry any staged change? A missing facts entry, or one with
 *  no new/deleted flag and no `was.*` value, is unchanged. */
export function hasRowChange(f: LaneRowFacts | undefined): boolean {
  if (!f) return false;
  if (f.isNew || f.deleted) return true;
  const w = f.was;
  return (
    w.name != null ||
    w.code != null ||
    w.days != null ||
    w.start != null ||
    w.finish != null
  );
}

/** The single most descriptive tag for a changed row. Returns null when the
 *  row is unchanged. Precedence: structural (new/deleted) first, then the
 *  dominant field edit. */
export function changeTagFor(f: LaneRowFacts | undefined): ChangeTag | null {
  if (!f || !hasRowChange(f)) return null;
  if (f.isNew) return { kind: "new", label: "new" };
  if (f.deleted) return { kind: "deleted", label: "deleted" };
  const w = f.was;
  if (w.start != null || w.finish != null || w.days != null) {
    return { kind: "rescheduled", label: "rescheduled" };
  }
  if (w.code != null) return { kind: "recoded", label: "re-coded" };
  if (w.name != null) return { kind: "renamed", label: "renamed" };
  return { kind: "edited", label: "edited" };
}
